import { ISPService, ILibsOptions, LibsOrderBy } from "../interfaces/ISPService";
import { ISPLists } from "../interfaces/SPEntities";
import { ISPHttpClientOptions } from "@microsoft/sp-http";
import authModule from "../../../utilities/AuthModule";
import { axiosRetryGet, axiosRetryPost } from "../../../utilities/AxiosRetryUtil";

export default class SPService implements ISPService {
  private _webAbsoluteUrl: string;

  constructor(webAbsoluteUrl?: string) {
    this._webAbsoluteUrl = webAbsoluteUrl;
  }

  public async getLibs(options?: ILibsOptions): Promise<ISPLists> {
    let filtered: boolean;
    let queryUrl: string = `${this._webAbsoluteUrl}/_api/web/lists?$select=Title,id,BaseTemplate`;

    if (options.orderBy) {
      queryUrl += `&$orderby=${options.orderBy === LibsOrderBy.Id ? "Id" : "Title"}`;
    }

    if (options.filter) {
      queryUrl += `&$filter=${encodeURIComponent(options.filter)}`;
    } else {
      if (options.baseTemplate) {
        queryUrl += `&$filter=BaseTemplate eq ${options.baseTemplate}`;
        filtered = true;
      }

      if (options.includeHidden === false) {
        queryUrl += filtered ? " and Hidden eq false" : "&$filter=Hidden eq false";
        filtered = true;
      }
    }

    const accessToken = await authModule.getSharePointAccessToken();
    const result = await axiosRetryGet(queryUrl, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (result?.data) {
      return result?.data as Promise<ISPLists>;
    } else {
      return null;
    }
  }

  public async getListItems(
    filterText: string,
    listId: string,
    internalColumnName: string,
    keyInternalColumnName?: string,
    webUrl?: string,
    filter?: string,
    substringSearch: boolean = false
  ): Promise<any[]> {
    let filterQuery: string;
    if (!(filterText?.trim() === "*")) {
      filterQuery = substringSearch // JJ - 20200613 - find by substring as an option
        ? `substringof('${encodeURIComponent(filterText.replace("'", "''"))}',${internalColumnName})${filter ? " and " + filter : ""}`
        : `startswith(${internalColumnName},'${encodeURIComponent(filterText.replace("'", "''"))}')${filter ? " and " + filter : ""}`;
    }

    try {
      const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
      let apiUrl = `${webAbsoluteUrl}/_api/web/lists('${listId}')/items?$select=${keyInternalColumnName || "Id"},${internalColumnName}`;

      if (filterQuery) {
        apiUrl += `&$filter=${filterQuery}`;
      }

      const accessToken = await authModule.getSharePointAccessToken();
      const result = await axiosRetryGet(apiUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (result?.data && result?.data.value && result?.data.value.length > 0) {
        return result?.data.value;
      }

      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getListItemsForListItemPicker(
    filterText: string,
    listId: string,
    internalColumnName: string,
    keyInternalColumnName?: string,
    webUrl?: string,
    filterList?: string
  ): Promise<any[]> {
    let _filter: string = `$filter=startswith(${internalColumnName},'${encodeURIComponent(
      filterText.replace("'", "''")
    )}') `;
    let costumfilter: string = filterList ? `and ${filterList}` : "";
    let _top = " &$top=2000";

    // test wild character "*"  if "*" load first 30 items
    if ((filterText.trim().indexOf("*") == 0 && filterText.trim().length == 1) || filterText.trim().length == 0) {
      _filter = "";
      costumfilter = filterList ? `$filter=${filterList}&` : "";
      _top = `$top=500`;
    }

    try {
      const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
      const apiUrl = `${webAbsoluteUrl}/_api/web/lists('${listId}')/items?$orderby=${internalColumnName}&$select=${keyInternalColumnName ||
        "Id"},${internalColumnName}&${_filter}${costumfilter}${_top}`;

      const accessToken = await authModule.getSharePointAccessToken();
      const result = await axiosRetryGet(apiUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (result?.data && result?.data.value && result?.data.value.length > 0) {
        return result?.data.value;
      }
      return [];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getListItemAttachments(listId: string, itemId: number, webUrl?: string): Promise<any[]> {
    try {
      const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
      const apiUrl = `${webAbsoluteUrl}/_api/web/lists(@listId)/items(@itemId)/AttachmentFiles?@listId=guid'${encodeURIComponent(
        listId
      )}'&@itemId=${encodeURIComponent(String(itemId))}`;

      const accessToken = await authModule.getSharePointAccessToken();
      const result = await axiosRetryGet(apiUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (result?.data && result?.data.value) {
        return result?.data.value;
      }

      return null;
    } catch (error) {
      console.dir(error);
      return Promise.reject(error);
    }
  }

  public async deleteAttachment(fileName: string, listId: string, itemId: number, webUrl?: string): Promise<void> {
    try {
      const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
      const apiUrl = `${webAbsoluteUrl}/_api/web/lists(@listId)/items(@itemId)/AttachmentFiles/getByFileName(@fileName)/RecycleObject?@listId=guid'${encodeURIComponent(
        listId
      )}'&@itemId=${encodeURIComponent(String(itemId))}&@fileName='${encodeURIComponent(
        fileName.replace(/'/g, "''")
      )}'`;

      const accessToken = await authModule.getSharePointAccessToken();
      await axiosRetryPost(apiUrl, null, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-HTTP-Method": "DELETE"
        }
      });
    } catch (error) {
      console.dir(error);
      return Promise.reject(error);
    }
  }

  public async addAttachment(
    listId: string,
    itemId: number,
    fileName: string,
    file: File,
    webUrl?: string
  ): Promise<void> {
    try {
      // Remove special characters in FileName
      fileName = fileName.replace(/[^\.\w\s\&\-]/gi, "");
      // Check if attachment exists
      const fileExists = await this.checkAttachmentExists(listId, itemId, fileName, webUrl);
      // Delete attachment if it exists
      if (fileExists) {
        await this.deleteAttachment(fileName, listId, itemId, webUrl);
      }
      // Add attachment
      const spOpts: ISPHttpClientOptions = {
        body: file
      };
      const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
      const apiUrl = `${webAbsoluteUrl}/_api/web/lists(@listId)/items(@itemId)/AttachmentFiles/add(FileName=@fileName)?@listId=guid'${encodeURIComponent(
        listId
      )}'&@itemId=${encodeURIComponent(String(itemId))}&@fileName='${encodeURIComponent(
        fileName.replace(/'/g, "''")
      )}'`;

      const accessToken = await authModule.getSharePointAccessToken();
      await axiosRetryPost(apiUrl, file, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getAttachment(listId: string, itemId: number, fileName: string, webUrl?: string): Promise<any> {
    const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
    const apiUrl = `${webAbsoluteUrl}/_api/web/lists(@listId)/items(@itemId)/AttachmentFiles/GetByFileBame(@fileName))?@listId=guid'${encodeURIComponent(
      listId
    )}'&@itemId=${encodeURIComponent(String(itemId))}&@fileName='${encodeURIComponent(fileName.replace(/'/g, "''"))}'`;

    const accessToken = await authModule.getSharePointAccessToken();
    const result = await axiosRetryGet(apiUrl, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (result?.data) {
      return result?.data;
    }

    return null;
  }

  public async checkAttachmentExists(listId: string, itemId: number, fileName: string, webUrl?: string): Promise<any> {
    try {
      const listServerRelativeUrl = await this.getListServerRelativeUrl(listId, webUrl);
      const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
      const fileServerRelativeUrl = `${listServerRelativeUrl}/Attachments/${itemId}/${fileName}`;
      const apiUrl = `${webAbsoluteUrl}/_api/web/getfilebyserverrelativeurl(@url)/Exists?@url='${encodeURIComponent(
        fileServerRelativeUrl.replace(/'/g, "''")
      )}'`;

      const accessToken = await authModule.getSharePointAccessToken();
      const result = await axiosRetryGet(apiUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (result?.data) {
        return result?.data.value;
      }

      return false;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getListName(listId: string, webUrl?: string): Promise<string> {
    const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
    const apiUrl = `${webAbsoluteUrl}/_api/web/lists(@listId)/RootFolder/Name?@listId=guid'${encodeURIComponent(
      listId
    )}'`;

    const accessToken = await authModule.getSharePointAccessToken();
    const result = await axiosRetryGet(apiUrl, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (result?.data) {
      return result?.data.value;
    }

    return null;
  }

  public async getListServerRelativeUrl(listId: string, webUrl?: string): Promise<string> {
    const webAbsoluteUrl = !webUrl ? this._webAbsoluteUrl : webUrl;
    const apiUrl = `${webAbsoluteUrl}/_api/web/lists(@listId)/RootFolder/ServerRelativeUrl?@listId=guid'${encodeURIComponent(
      listId
    )}'`;

    const accessToken = await authModule.getSharePointAccessToken();
    const result = await axiosRetryGet(apiUrl, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (result?.data) {
      return result?.data.value;
    }

    return null;
  }
}
